import type { HTMLAttributes } from 'react';
import {
  formatInstant,
  formatPlainDate,
  formatRelative,
  isPlainDate,
  timeZoneLabel,
  useTimeZone,
  type InstantStyle,
} from '@/lib/time';
import { cx } from '../internal/cx';
import styles from './DateTime.module.css';

export interface DateTimeDisplayProps extends Omit<
  HTMLAttributes<HTMLTimeElement>,
  'children' | 'style'
> {
  /** ISO-8601 UTC instant. */
  value: string | null | undefined;
  /** Display style; `relative` shows e.g. "5 minutes ago". */
  style?: InstantStyle | 'relative';
  /** Zone to display in. Defaults to useTimeZone().timeZone. */
  timeZone?: string;
  /** Append the zone abbreviation (e.g. "EDT"). */
  showZone?: boolean;
  /** Rendered when value is empty or invalid. */
  fallback?: string;
}

/**
 * Renders an instant in the effective time zone as <time dateTime=...>. Hovering shows the full
 * date, zone and UTC value (title), so the exact moment is always recoverable.
 */
export function DateTimeDisplay({
  value,
  style = 'datetime',
  timeZone: timeZoneProp,
  showZone = false,
  fallback = '—',
  className,
  ...rest
}: DateTimeDisplayProps) {
  const context = useTimeZone();
  const timeZone = timeZoneProp ?? context.timeZone;
  const locale = context.locale;
  const date = value ? new Date(value) : null;

  if (!value || !date || Number.isNaN(date.getTime())) {
    return <span className={cx(styles.muted, className)}>{fallback}</span>;
  }

  const text =
    style === 'relative'
      ? formatRelative(value, undefined, locale)
      : formatInstant(value, timeZone, { style, locale, showZone });
  const full = `${formatInstant(value, timeZone, { style: 'datetime-long', locale })} ${timeZoneLabel(timeZone, value, locale)} (${timeZone}) · UTC ${date.toISOString()}`;

  return (
    <time
      dateTime={date.toISOString()}
      title={full}
      className={cx(styles.time, className)}
      {...rest}
    >
      {text}
    </time>
  );
}

export interface PlainDateDisplayProps extends Omit<
  HTMLAttributes<HTMLTimeElement>,
  'children' | 'style'
> {
  /** Plain calendar date "YYYY-MM-DD" (no time zone). */
  value: string | null | undefined;
  style?: 'short' | 'medium' | 'long' | 'full';
  fallback?: string;
}

export function PlainDateDisplay({
  value,
  style = 'medium',
  fallback = '—',
  className,
  ...rest
}: PlainDateDisplayProps) {
  const { locale } = useTimeZone();
  if (!value || !isPlainDate(value)) {
    return <span className={cx(styles.muted, className)}>{fallback}</span>;
  }
  return (
    <time
      dateTime={value}
      title={formatPlainDate(value, { locale, style: 'full' })}
      className={cx(styles.time, className)}
      {...rest}
    >
      {formatPlainDate(value, { locale, style })}
    </time>
  );
}
